import type {
  PurchaseOrderFormValues,
  PurchaseOrderHistoryItem,
  PurchaseOrderView,
  PurchaseReceiptView,
} from "@cx/contracts";
import type { FreightMode } from "@cx/domain";
import { lotDraftProblem, lotPayload, type LotDraft } from "../stock/labels";

// ===== Pedidos de compra: estado do formulário, recebimento e rótulos (T2.6, RF-SUP-03) =====

/** Linha do formulário. `unitPriceCents` nulo = último custo do fornecedor / custo do produto (o servidor resolve). */
export type PurchaseItemRow = {
  key: string;
  productId: string;
  description: string;
  code: string | null;
  unit: string;
  qtyQ4: number | null;
  unitPriceCents: number | null;
  ipiPct4: number | null;
};

export type PurchaseOrderForm = {
  date: string;
  expectedDate: string;
  supplier: { id: string; name: string } | null;
  purchaseRef: string;
  warehouseId: string;
  items: PurchaseItemRow[];
  discountMode: "value" | "pct";
  discountCents: number | null;
  discountPct4: number | null;
  freightCents: number | null;
  otherExpensesCents: number | null;
  icmsStCents: number | null;
  paymentTermId: string;
  financialCategoryId: string;
  carrierId: string;
  freightMode: FreightMode;
  notes: string;
  internalNotes: string;
};

let seq = 0;
export const rowKey = () => `p${++seq}`;

export function emptyPurchaseForm(today: string): PurchaseOrderForm {
  return {
    date: today,
    expectedDate: "",
    supplier: null,
    purchaseRef: "",
    warehouseId: "",
    items: [],
    discountMode: "value",
    discountCents: null,
    discountPct4: null,
    freightCents: null,
    otherExpensesCents: null,
    icmsStCents: null,
    paymentTermId: "",
    financialCategoryId: "",
    carrierId: "",
    freightMode: 9,
    notes: "",
    internalNotes: "",
  };
}

/** Pedido gravado → formulário de edição (preços congelados). */
export function purchaseFormFromView(o: PurchaseOrderView): PurchaseOrderForm {
  return {
    date: o.date,
    expectedDate: o.expectedDate ?? "",
    supplier: { id: o.supplier.id, name: o.supplier.name },
    purchaseRef: o.purchaseRef ?? "",
    warehouseId: o.warehouse?.id ?? "",
    items: o.items.map((i) => ({
      key: rowKey(),
      productId: i.productId,
      description: i.description,
      code: i.code,
      unit: i.unit ?? "",
      qtyQ4: i.qtyQ4,
      unitPriceCents: i.unitPriceCents,
      ipiPct4: i.ipiPct4,
    })),
    discountMode: o.discountPct4 !== null ? "pct" : "value",
    discountCents: o.discountPct4 !== null ? null : o.discountCents,
    discountPct4: o.discountPct4,
    freightCents: o.freightCents,
    otherExpensesCents: o.otherExpensesCents,
    icmsStCents: o.icmsStCents,
    paymentTermId: o.paymentTerm?.id ?? "",
    financialCategoryId: o.financialCategory?.id ?? "",
    carrierId: o.carrier?.id ?? "",
    freightMode: o.freightMode,
    notes: o.notes ?? "",
    internalNotes: o.internalNotes ?? "",
  };
}

const orNull = (s: string) => (s.trim() ? s.trim() : null);

/** Formulário → corpo de POST/PUT /purchase-orders (o servidor valida tudo de novo). */
export function purchaseBody(f: PurchaseOrderForm): PurchaseOrderFormValues {
  return {
    supplierId: f.supplier?.id ?? "",
    date: f.date,
    expectedDate: orNull(f.expectedDate),
    purchaseRef: orNull(f.purchaseRef),
    warehouseId: orNull(f.warehouseId),
    items: f.items.map((i) => ({
      productId: i.productId,
      qtyQ4: i.qtyQ4 ?? 0,
      unitPriceCents: i.unitPriceCents,
      ipiPct4: i.ipiPct4 ?? 0,
    })),
    discountPct4: f.discountMode === "pct" ? (f.discountPct4 ?? 0) : null,
    discountCents: f.discountMode === "value" ? (f.discountCents ?? 0) : 0,
    freightCents: f.freightCents ?? 0,
    otherExpensesCents: f.otherExpensesCents ?? 0,
    icmsStCents: f.icmsStCents ?? 0,
    paymentTermId: orNull(f.paymentTermId),
    financialCategoryId: orNull(f.financialCategoryId),
    carrierId: orNull(f.carrierId),
    freightMode: f.freightMode,
    notes: orNull(f.notes),
    internalNotes: orNull(f.internalNotes),
  };
}

/** Corpo da prévia: null enquanto falta fornecedor ou item, ou há quantidade vazia. */
export function purchasePreviewBody(f: PurchaseOrderForm): PurchaseOrderFormValues | null {
  if (!f.supplier || f.items.length === 0 || f.items.some((i) => !i.qtyQ4)) return null;
  return { ...purchaseBody(f), notes: null, internalNotes: null, purchaseRef: null };
}

/** Erros de campo da API → linha do item (`items.0.qtyQ4` fica na linha 0). */
export function itemError(errors: Record<string, string>, index: number): string | undefined {
  const prefix = `items.${index}.`;
  const k = Object.keys(errors).find((e) => e.startsWith(prefix));
  return k ? errors[k] : undefined;
}

// ---------- recebimento ----------

/** Quantidade a receber por item (padrão = saldo). */
/** Lote digitado no "Receber" (T3.2): só para produto com controle de lotes; o que não for a lote fica "Sem lote". */
export type ReceiveLotRow = LotDraft & { qtyQ4: number | null };
export type ReceiveRow = { itemId: string; qtyQ4: number | null; lots?: ReceiveLotRow[] };

const filledLots = (lots: readonly ReceiveLotRow[] | undefined) =>
  (lots ?? []).filter((l) => l.code.trim() !== "" || (l.qtyQ4 ?? 0) > 0);

export function receiveDefaults(o: Pick<PurchaseOrderView, "items">): ReceiveRow[] {
  return o.items.filter((i) => i.remainingQ4 > 0).map((i) => ({ itemId: i.id, qtyQ4: i.remainingQ4 }));
}

/** Problema do recebimento na tela (o servidor confere de novo): nada a receber ou acima do saldo. */
export function receiveProblem(o: Pick<PurchaseOrderView, "items">, rows: ReceiveRow[]): string | null {
  const byId = new Map(o.items.map((i) => [i.id, i]));
  let any = false;
  for (const r of rows) {
    const q = r.qtyQ4 ?? 0;
    if (q <= 0) continue;
    any = true;
    const it = byId.get(r.itemId);
    if (it && q > it.remainingQ4) return `"${it.description}": a quantidade passa do saldo a receber.`;
    const name = `"${it?.description ?? r.itemId}"`;
    let sum = 0;
    for (const l of filledLots(r.lots)) {
      const p = lotDraftProblem(l, true);
      if (p) return `${name}: ${p}`;
      if ((l.qtyQ4 ?? 0) <= 0) return `${name}, lote ${l.code.trim()}: informe a quantidade.`;
      sum += l.qtyQ4!;
    }
    if (sum > q) return `${name}: a soma dos lotes passa da quantidade recebida.`;
  }
  return any ? null : "Informe a quantidade recebida de ao menos um item.";
}

export function receiveItems(rows: ReceiveRow[]) {
  return rows
    .filter((r) => (r.qtyQ4 ?? 0) > 0)
    .map((r) => {
      const lots = filledLots(r.lots).map((l) => ({ ...lotPayload(l)!, qtyQ4: l.qtyQ4! }));
      return { itemId: r.itemId, qtyQ4: r.qtyQ4!, ...(lots.length ? { lots } : {}) };
    });
}

export const RECEIPT_SOURCE_LABELS: Readonly<Record<PurchaseReceiptView["source"], string>> = {
  manual: "Recebimento",
  status: "Mudança de situação",
  inbound_dfe: "NF-e de entrada",
};

export const PROGRESS_LABELS: Readonly<Record<PurchaseOrderView["progress"], string>> = {
  none: "Nada recebido",
  partial: "Recebido em parte",
  complete: "Recebido",
};

/** "Recebimento 2 · NF-e nº 123" para a linha do tempo. */
export function receiptTitle(r: Pick<PurchaseReceiptView, "number" | "source" | "inboundDfe">): string {
  const base = `Recebimento ${r.number}`;
  if (r.source !== "inbound_dfe") return base;
  const n = r.inboundDfe?.number;
  return n !== null && n !== undefined ? `${base} · NF-e nº ${n}` : `${base} · NF-e`;
}

/** O que o recebimento lançou: "Estoque e 2 contas", "Só estoque", "Lançado pela NF-e". */
export function receiptEffects(
  r: Pick<PurchaseReceiptView, "source" | "movements" | "titles" | "stockPosted">,
): string {
  if (r.source === "inbound_dfe") return "Estoque e contas lançados pela NF-e";
  const parts = [
    ...(r.stockPosted ? ["estoque"] : []),
    ...(r.titles ? [r.titles === 1 ? "1 conta a pagar" : `${r.titles} contas a pagar`] : []),
  ];
  if (parts.length === 0) return "Sem lançamentos";
  const s = parts.join(" e ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Linha do histórico de situações: "Em aberto → Em andamento". */
export function purchaseHistoryLabel(h: Pick<PurchaseOrderHistoryItem, "from" | "to">): string {
  return h.from ? `${h.from.label} → ${h.to.label}` : h.to.label;
}
